import React, { useLayoutEffect, useRef, useState } from "react";

const BUTTON_MARQUEE_STYLE_ID = "button-marquee-text-style";

function ensureMarqueeStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(BUTTON_MARQUEE_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = BUTTON_MARQUEE_STYLE_ID;
  style.textContent = `
    @keyframes button-marquee-text {
      0%, 12% { transform: translateX(0); }
      88%, 100% { transform: translateX(var(--marquee-distance, 0px)); }
    }
    button[class*="order-button-"] > span {
      display: block !important;
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }
  `;
  document.head.appendChild(style);
}

/** Scrolls label horizontally when text overflows the button width. */
export function MarqueeButtonText({ text }: { text: string }) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useLayoutEffect(() => {
    ensureMarqueeStyles();

    const measure = () => {
      const container = containerRef.current;
      const content = contentRef.current;
      if (!container || !content) return;
      const overflow = content.scrollWidth - container.clientWidth;
      setOverflowPx(overflow > 2 ? overflow : 0);
    };

    measure();

    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [text]);

  const isOverflowing = overflowPx > 0;
  // ~28px/s, with a short pause at each end via keyframe holds
  const durationSec = Math.min(12, Math.max(2.5, overflowPx / 28));

  return (
    <span
      ref={containerRef}
      style={{
        display: "block",
        width: "100%",
        overflow: "hidden",
        lineHeight: 1.2,
        textAlign: isOverflowing ? "left" : "center",
      }}
    >
      <span
        ref={contentRef}
        style={{
          display: "inline-block",
          whiteSpace: "nowrap",
          maxWidth: isOverflowing ? undefined : "100%",
          ...(isOverflowing
            ? {
                ["--marquee-distance" as string]: `-${overflowPx}px`,
                animation: `button-marquee-text ${durationSec}s ease-in-out infinite alternate`,
                willChange: "transform",
              }
            : {}),
        }}
      >
        {text}
      </span>
    </span>
  );
}
